import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge, Card, CardBody, Col, Input, Row, Spinner, Table } from "reactstrap";
import ExpenseManagerService from "../../../Services/expenseManager/ExpenseManagerService";
import { formatMoney } from "../../../utils/formatMoney";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { formatShortDate, formatMonth, statusMeta } from "./expenseManagerUtils";

const KPI = ({ label, value, hint }) => (
  <Card className="em-kpi">
    <CardBody>
      <div className="em-kpi-label">{label}</div>
      <div className="em-kpi-value">{value}</div>
      {hint ? <div className="em-kpi-hint">{hint}</div> : null}
    </CardBody>
  </Card>
);

const Section = ({ title, children }) => (
  <div className="em-section">
    <h6 className="em-section-title">{title}</h6>
    {children}
  </div>
);

export default function DashboardTab({ meta }) {
  const [companyId, setCompanyId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const params = useMemo(() => {
    const next = {};
    if (companyId) next.company_id = [companyId];
    if (from) next.expense_date_from = from;
    if (to) next.expense_date_to = to;
    return next;
  }, [companyId, from, to]);

  const query = useQuery({
    queryKey: ["expenseManagerDashboard", params],
    queryFn: () => ExpenseManagerService.getDashboard(params),
  });

  if (query.isLoading) {
    return <div className="text-center py-5"><Spinner /></div>;
  }
  if (query.isError) {
    return <div className="alert alert-danger">{getApiErrorMessage(query.error, "Could not load the dashboard.")}</div>;
  }

  const data = query.data?.data ?? query.data ?? {};
  const kpis = data.kpis || {};
  const payment = data.payment_breakdown || {};
  const multiCurrency = (data.by_currency || []).length > 1;

  return (
    <div className="em-dashboard">
      <Row className="g-2 mb-3">
        <Col md={3} sm={6}>
          <label className="form-label small mb-1" htmlFor="em-dash-company">Company</label>
          <Input
            id="em-dash-company"
            type="select"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <option value="">All companies</option>
            {meta.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Input>
        </Col>
        <Col md={3} sm={6}>
          <label className="form-label small mb-1" htmlFor="em-dash-from">From</label>
          <Input id="em-dash-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Col>
        <Col md={3} sm={6}>
          <label className="form-label small mb-1" htmlFor="em-dash-to">To</label>
          <Input id="em-dash-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col lg={2} md={4} sm={6}><KPI label="Expenses" value={kpis.expense_count ?? 0} /></Col>
        <Col lg={2} md={4} sm={6}><KPI label="Gross (base)" value={formatMoney(kpis.base_gross, meta.companies[0]?.base_currency)} /></Col>
        <Col lg={2} md={4} sm={6}><KPI label="Net" value={formatMoney(kpis.net)} /></Col>
        <Col lg={2} md={4} sm={6}><KPI label="Tax" value={formatMoney(kpis.tax)} /></Col>
        <Col lg={2} md={4} sm={6}><KPI label="Paid" value={formatMoney(kpis.paid)} /></Col>
        <Col lg={2} md={4} sm={6}><KPI label="Open" value={formatMoney(kpis.open)} /></Col>
      </Row>

      <Row className="g-2 mb-3">
        <Col lg={3} md={6}>
          <KPI
            label="Awaiting approval"
            value={kpis.awaiting_approval_count ?? 0}
            hint={formatMoney(kpis.awaiting_approval_total)}
          />
        </Col>
        <Col lg={3} md={6}>
          <KPI
            label="Overdue"
            value={(data.overdue || []).length}
            hint="Approved, past due date"
          />
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={6}>
          <Section title="Payment breakdown">
            <Table size="sm" borderless className="mb-0">
              <thead>
                <tr><th>Status</th><th className="text-end">Count</th><th className="text-end">Paid</th><th className="text-end">Gross</th></tr>
              </thead>
              <tbody>
                {["unpaid", "partially_paid", "paid"].map((key) => (
                  <tr key={key}>
                    <td>
                      <Badge color={statusMeta(key).color} pill className="me-1">{statusMeta(key).label}</Badge>
                    </td>
                    <td className="text-end">{payment[key]?.expense_count ?? 0}</td>
                    <td className="text-end">{formatMoney(payment[key]?.paid)}</td>
                    <td className="text-end">{formatMoney(payment[key]?.gross)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>

        <Col lg={6}>
          <Section title="By currency">
            <Table size="sm" borderless className="mb-0">
              <thead>
                <tr><th>Currency</th><th className="text-end">Count</th><th className="text-end">Gross</th><th className="text-end">Tax</th></tr>
              </thead>
              <tbody>
                {(data.by_currency || []).length === 0 && <tr><td colSpan="4" className="text-muted">No data.</td></tr>}
                {(data.by_currency || []).map((row) => (
                  <tr key={row.currency_code}>
                    <td>{row.currency_code}</td>
                    <td className="text-end">{row.expense_count}</td>
                    <td className="text-end">{formatMoney(row.gross, row.currency_code)}</td>
                    <td className="text-end">{formatMoney(row.tax, row.currency_code)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>

        <Col lg={6}>
          <Section title="Top categories">
            <Table size="sm" borderless className="mb-0">
              <tbody>
                {(data.by_category || []).length === 0 && <tr><td className="text-muted">No data.</td></tr>}
                {(data.by_category || []).map((row) => (
                  <tr key={`${row.category_id}-${row.name}`}>
                    <td>{row.name}</td>
                    <td className="text-end text-muted">{row.expense_count}×</td>
                    <td className="text-end">{formatMoney(row.total, multiCurrency ? undefined : meta.companies[0]?.base_currency)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>

        <Col lg={6}>
          <Section title="Top suppliers">
            <Table size="sm" borderless className="mb-0">
              <tbody>
                {(data.by_counterparty || []).length === 0 && <tr><td className="text-muted">No data.</td></tr>}
                {(data.by_counterparty || []).map((row) => (
                  <tr key={`${row.counterparty_id}-${row.name}`}>
                    <td>{row.name}</td>
                    <td className="text-end text-muted">{row.expense_count}×</td>
                    <td className="text-end">{formatMoney(row.total, multiCurrency ? undefined : meta.companies[0]?.base_currency)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>

        <Col lg={4}>
          <Section title="Monthly trend">
            <Table size="sm" borderless className="mb-0">
              <tbody>
                {(data.monthly_trend || []).length === 0 && <tr><td className="text-muted">No data.</td></tr>}
                {(data.monthly_trend || []).map((row) => (
                  <tr key={row.month}>
                    <td>{formatMonth(row.month)}</td>
                    <td className="text-end text-muted">{row.expense_count}×</td>
                    <td className="text-end">{formatMoney(row.total, meta.companies[0]?.base_currency)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>

        <Col lg={4}>
          <Section title="Upcoming due (30 days)">
            <Table size="sm" borderless className="mb-0">
              <tbody>
                {(data.upcoming_due || []).length === 0 && <tr><td className="text-muted">Nothing due.</td></tr>}
                {(data.upcoming_due || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.title}<div className="text-muted small">{row.reference} · {formatShortDate(row.due_date)}</div></td>
                    <td className="text-end">{formatMoney(row.gross_amount, row.currency_code)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>

        <Col lg={4}>
          <Section title="Recent">
            <Table size="sm" borderless className="mb-0">
              <tbody>
                {(data.recent || []).length === 0 && <tr><td className="text-muted">No expenses.</td></tr>}
                {(data.recent || []).map((row) => (
                  <tr key={row.id}>
                    <td>{row.title}<div className="text-muted small">{row.reference} · {formatShortDate(row.expense_date)}</div></td>
                    <td className="text-end"><Badge color={statusMeta(row.status).color} pill>{statusMeta(row.status).label}</Badge></td>
                    <td className="text-end">{formatMoney(row.gross_amount, row.currency_code)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </Col>
      </Row>
    </div>
  );
}
